import React, { useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import connect from 'react-redux/es/connect/connect';
import withReactContent from 'sweetalert2-react-content';
import swal from 'sweetalert2';
import { toast } from 'react-toastify';

import './EventThemes.sass';
import {
  deleteEventStyleTheme,
  getEventStyleTheme,
  updateEventStyleTheme,
} from '../../../../../store/actions/eventsActions';
import { getDefaultStyles } from '../../../../../helpers/getDefaultStyles';
import { ThemeSettings } from '../../../../../components/Themes/ThemeSettings/ThemeSettings';
import NewLoader from '../../../../../components/NewLoader/NewLoader';
import CheckBox from '../../../../../components/UI/Checkbox/CheckBox';
import { ThemeFieldSettings } from '../../../../../components/Themes/ThemeSettings/ThemeFieldSettings';
import { getHeaders } from '../../../../../helpers/getHeaders';
import axios from '../../../../../axios-instance';
import { AddFieldModal } from '../../../../../components/Themes/Modals/AddFieldModal';

const MySwal = withReactContent(swal);

export const EventThemes = ({
  getEventStyleTheme,
  updateEventStyleTheme,
  currentEvent,
  loading,
  deleteEventStyleTheme,
  token,
}) => {
  const {
    fontFamily,
    primaryColor,
    primarySemiLightColor,
    primaryLightColor,
    secondaryColor,
    secondaryLightColor,
    backgroundColor,
    textColor,
    secondaryTextColor,
  } = getDefaultStyles(document.getElementById('body'));

  const defaultTheme = {
    fontFamily,
    primaryColor,
    primarySemiLightColor,
    primaryLightColor,
    secondaryColor,
    secondaryLightColor,
    backgroundColor,
    primaryTextColor: textColor,
    secondaryTextColor,
  };

  const [styleTheme, setStyleTheme] = useState(defaultTheme);
  const [fieldTheme, setFieldTheme] = useState([]);
  const [isThemeEdited, setThemeEdited] = useState(false);
  const [isPromoterThemeByDefault, setPromoterThemeByDefault] = useState(false);
  const [isFieldsLoading, setFieldsLoading] = useState(true);
  const validateTheme = (styles) => {
      if (!styles) {
        var defStyles = getDefaultStyles(document.getElementById('body'));
        defStyles.isDefault = true;
        return defStyles;
      }
      return styles;
  };

  const getFieldTheme = async () => {
    setFieldsLoading(true);

    return axios
      .get(`/TransactionFields/Event/${currentEvent.id}`, {
        headers: getHeaders(token),
      })
      .then((response) => {
        if (response.data.isSuccess) {
          return response.data.result;
        }
        throw new Error(response.data.errorMessage);
      })
      .catch((err) => {
        toast.error('Fields theme not received');
      })
      .finally(() => {
        setFieldsLoading(false);
      });
  };

  const getData = async () => {
    try {
      const [fields, styles] = await Promise.all([
        getFieldTheme(),
        getEventStyleTheme(currentEvent.id),
      ]);
      const validatedStyles = validateTheme(styles);
      setStyleTheme(validatedStyles);
      setPromoterThemeByDefault(validatedStyles.isDefault);

      setFieldTheme(fields);
    } catch (error) {
      console.log(error);
    }
  };

  const handleChangeFieldSetting = (type, key, value, id) => {
    const oldData = fieldTheme.find((ft) => ft.id === id);
    const newData = {
      ...oldData,
      [key]: value,
    };

    try {
      axios
        .put(`/TransactionFields/Event/${currentEvent.id}`, newData, {
          headers: getHeaders(token),
        })
        .then((response) => {
          if (response.data.isSuccess) {
            setFieldTheme((prev) => {
              return prev.map((f) => {
                if (f.id !== id) return f;

                return {
                  ...f,
                  [key]: value,
                };
              });
            });

            return response.data.result;
          }
          throw new Error(response.data.errorMessage);
        })
        .catch((err) => {
          toast.error('Fields theme not saved');
        });
    } catch (error) {
      toast.error('Something went wrong');
    }
  };

  const handleChangeCompanyData = async (key, value, id) => {
    // type 9 || 10
    try {
      await Promise.all([
        handleChangeFieldSetting(9, key, value, id),
        handleChangeFieldSetting(10, key, value, id),
      ]);
    } catch (error) {
      toast.error('Fields theme not saved');
    }
  };

  const handleChangeStyleSetting = (name, value) => {
    setThemeEdited(true);
    setStyleTheme((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleChangeDefaulteTheme = () => {
    setThemeEdited(true);
    setPromoterThemeByDefault((prev) => !prev);
  };

  const handleAddFieldSubmit = (data) => {
    setFieldsLoading(true);

    try {
      axios
        .post(`/TransactionFields/Event/${currentEvent.id}`, data, {
          headers: getHeaders(token),
        })
        .then((response) => {
          if (response.data.isSuccess) {
            toast.success('Field is added successfully');
            MySwal.close();

            getData();

            return response.data.result;
          }
          throw new Error(response.data.errorMessage);
        })
        .catch((err) => {
          toast.error('Fields theme not added');
          setFieldsLoading(false);
        });
    } catch (error) {
      toast.error('Something went wrong');
    }
  }

  const addField = () => {
    MySwal.fire({
      html: <AddFieldModal handleSubmit={handleAddFieldSubmit} />,
      showConfirmButton: false,
      width: 'max-content',
    });
  };

  const removeField = async (field) => {
    setFieldsLoading(true);

    return axios
      .delete(`/TransactionFields/Event/${currentEvent.id}/${field.id}`, {
        headers: getHeaders(token),
      })
      .then((response) => {
        if (response.data.isSuccess) {
          toast.success('Field is removed successfully');

          getData();

          return response.data.result;
        }
        throw new Error(response.data.errorMessage);
      })
      .catch((err) => {
        toast.error('Fields theme not received');
        setFieldsLoading(false);
      });
  }

  const saveStyleTheme = () => {
    MySwal.fire({
      title: 'Do you want to save changes?',
      customClass: 'swal2-modal',
      showCancelButton: true,
      confirmButtonText: 'Save',
      reverseButtons: true,
    }).then((result) => {
      if (result.value) {
        if (isPromoterThemeByDefault) {
          deleteEventStyleTheme(currentEvent.id).then((data) => {
            setStyleTheme(data);
            setPromoterThemeByDefault(data.isDefault);
          });
        } else {
          updateEventStyleTheme(currentEvent.id, styleTheme);
        }

        setThemeEdited(false);
      }
    });
  };

  useEffect(() => {
    if (currentEvent.id) {
      getData();
    }
  }, [currentEvent]);

  if (loading || isFieldsLoading) {
    return <NewLoader />;
  }

  return (
    <section className='event-themes'>
      <div className='event-themes__settings-container'>
        <div>
          <ThemeFieldSettings
            fieldTheme={fieldTheme}
            handleChangeCompanyData={handleChangeCompanyData}
            handleChangeFieldSetting={handleChangeFieldSetting}
            removeField={removeField}
          />
          <button
            type='button'
            className='btn-primary promoter-theme__btn'
            onClick={addField}
          >
            Add field
          </button>
        </div>

        <div>
          <div className='event-themes__settings-wrapper'>
            <ThemeSettings
              theme={styleTheme}
              handleChangeSetting={handleChangeStyleSetting}
            />
            {isPromoterThemeByDefault && (
              <div className='event-themes__settings-disabler'></div>
            )}
          </div>
          <div className='event-themes__buttons-container'>
            <CheckBox
              checked={isPromoterThemeByDefault}
              id='isPromoterThemeByDefault'
              name='isPromoterThemeByDefault'
              onChange={handleChangeDefaulteTheme}
            >
              <span className='input-behavior'>
                Set promoter theme by default
              </span>
            </CheckBox>

            <button
              type='button'
              className='btn-primary event-themes__save-styles'
              onClick={saveStyleTheme}
              disabled={!isThemeEdited}
            >
              Save colors
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

const mapStateToProps = ({ events, auth }) => ({
  loading: events.loading,
  theme: events.theme,
  currentEvent: events.currentEvent,
  token: auth.user.token,
});

const mapDispatchToProps = (dispatch) => ({
  getEventStyleTheme: (eventId) => dispatch(getEventStyleTheme(eventId)),
  updateEventStyleTheme: (eventId, data) =>
    dispatch(updateEventStyleTheme(eventId, data)),
  deleteEventStyleTheme: (eventId) => dispatch(deleteEventStyleTheme(eventId)),
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(EventThemes)
);
